import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { ProductTypeConfigValueItem } from 'constants/Constants'
import config from 'config'
import { useAuth0Authentication } from './useAuth0Authentication'

/*
    We look up product type by its value — a SKU introduced recently (e.g PG000001) —
    or its legacy code for old products pre-SKU (e.g H6CA)
*/
export const findProductType = (
    productTypes: ProductTypeConfigValueItem[] | undefined,
    productTypeCode: string,
): ProductTypeConfigValueItem | undefined =>
    productTypes?.find(item => item.value === productTypeCode || item.legacyCode === productTypeCode)


export const productTypeLabel = (
    productTypes: ProductTypeConfigValueItem[] | undefined,
    productTypeCode: string,
): string =>
    findProductType(productTypes, productTypeCode)?.label ?? productTypeCode

export interface IUseProductTypes {
    productTypes: ProductTypeConfigValueItem[] | undefined,
    isLoading: boolean,
    error: Error | null,
    getProductType: (productCode: string) => ProductTypeConfigValueItem | undefined,
    addProductType: (productType: ProductTypeConfigValueItem) => Promise<ProductTypeConfigValueItem>,
    isAdding: boolean,
    addError: Error | null,
    editProductType: (productType: ProductTypeConfigValueItem) => Promise<void>,
    isEditing: boolean,
    editError: Error | null,
    deleteProductType: (productType: ProductTypeConfigValueItem) => Promise<void>,
    isDeleting: boolean,
    deleteError: Error | null
}

// Separate API function for better testability and reusability
const fetchProductTypes = async (accessToken: string): Promise<ProductTypeConfigValueItem[]> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/product-types`,
        {
            method: 'GET',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
        }
    )

    if (!response.ok) {
        const message: string = response.status < 500 ? await response.text() : ''
        throw new Error(message || `Failed to fetch product types: ${response.statusText}`)
    }

    const productTypesResp: ProductTypeConfigValueItem[] = await response.json()
    return productTypesResp
        .map(item => ({ ...item, weightMode: item.weightMode ?? 'CONSTANT' }))
        .sort((a, b) => a.label.localeCompare(b.label))
}

// Separate API function for adding product types
const addProductTypeAPI = async (accessToken: string, productType: ProductTypeConfigValueItem): Promise<ProductTypeConfigValueItem> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/product-types`,
        {
            method: 'POST',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(productType)
        }
    )

    if (!response.ok) {
        const message: string = response.status < 500 ? await response.text() : ''
        throw new Error(message || `Failed to add product type: ${response.statusText}`)
    }

    const productTypeResp: ProductTypeConfigValueItem = await response.json()
    return productTypeResp
}

// Separate API function for editing product types
const editProductTypeAPI = async (accessToken: string, productType: ProductTypeConfigValueItem): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/product-types`,
        {
            method: 'PATCH',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(productType)
        }
    )

    if (!response.ok) {
        const message: string = response.status < 500 ? await response.text() : ''
        throw new Error(message || `Failed to edit product type: ${response.statusText}`)
    }
}

// Separate API function for deleting product types
const deleteProductTypeAPI = async (accessToken: string, productType: ProductTypeConfigValueItem): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/product-types`,
        {
            method: 'DELETE',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(productType)
        }
    )

    if (!response.ok) {
        const message: string = response.status < 500 ? await response.text() : ''
        throw new Error(message || `Failed to delete product type: ${response.statusText}`)
    }
}

export const useProductTypes = (): IUseProductTypes => {
    const { getAuth0AccessToken } = useAuth0Authentication()
    const queryClient = useQueryClient()

    const {
        data: productTypes,
        isLoading,
        error
    } = useQuery<ProductTypeConfigValueItem[], Error>({
        queryKey: ['productTypes'],
        queryFn: async () => {
            const accessToken = await getAuth0AccessToken()
            return fetchProductTypes(accessToken)
        },
    })

    const {
        mutateAsync: addProductType,
        isPending: isAdding,
        error: addError
    } = useMutation<ProductTypeConfigValueItem, Error, ProductTypeConfigValueItem>({
        mutationFn: async (productType: ProductTypeConfigValueItem) => {
            const accessToken = await getAuth0AccessToken()
            return addProductTypeAPI(accessToken, productType)
        },
        onSuccess: () => {
            // Invalidate and refetch product types after successful addition
            queryClient.invalidateQueries({ queryKey: ['productTypes'] })
        },
    })

    const {
        mutateAsync: editProductType,
        isPending: isEditing,
        error: editError
    } = useMutation<void, Error, ProductTypeConfigValueItem>({
        mutationFn: async (productType: ProductTypeConfigValueItem) => {
            const accessToken = await getAuth0AccessToken()
            return editProductTypeAPI(accessToken, productType)
        },
        onSuccess: () => {
            // Invalidate and refetch product types after successful edit
            queryClient.invalidateQueries({ queryKey: ['productTypes'] })
        }
    })

    const {
        mutateAsync: deleteProductType,
        isPending: isDeleting,
        error: deleteError
    } = useMutation<void, Error, ProductTypeConfigValueItem>({
        mutationFn: async (productType: ProductTypeConfigValueItem) => {
            const accessToken = await getAuth0AccessToken()
            return deleteProductTypeAPI(accessToken, productType)
        },
        onSuccess: () => {
            // Invalidate and refetch product types after successful deletion
            queryClient.invalidateQueries({ queryKey: ['productTypes'] })
        }
    })
 
    const getProductType = (productCode: string): ProductTypeConfigValueItem | undefined => {
        return findProductType(productTypes, productCode)
    }

    return {
        productTypes,
        isLoading,
        error: error || null,
        getProductType,
        addProductType,
        isAdding,
        addError: addError || null,
        editProductType,
        isEditing,
        editError: editError || null,
        deleteProductType,
        isDeleting,
        deleteError: deleteError || null
    }
}